import { useImperativeHandle, useRef, type Ref, type RefObject } from 'react';

import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { useDataTableConfigElement } from '../../hooks/useDataTableConfig.js';
import type { DataTableColumnSettingsModalRef } from '../components/Toolbar/ColumnSettings/column-settings-types.js';
import { DATA_TABLE_DETAILS_ROW_IDENTIFIER } from '../constants.js';
import type { Table } from '../hooks/useTable/types.js';
import type {
  DataTableColumnSettingsConfig,
  DataTableRef,
} from '../public.api.js';
import type {
  DataTableRowThreshold,
  DataTableThresholdProps,
} from './Thresholds/threshold-types.js';

/**
 * Extends the ref of the DataTable with additional configuration and control options.
 * @internal
 */
export function useDataTableConfigRef<TData>(
  forwardedRef: Ref<DataTableRef>,
  columnSettingsModalRef: RefObject<DataTableColumnSettingsModalRef | null>,
  table: Table<TData>,
  props: DataTableThresholdProps<TData>,
) {
  /*
   * Collect the refs and create an imperative handle in order to expose the
   * functions we need on the DataTable.
   */
  const containerRef = useRef<HTMLDivElement>(null);
  const rootRef = useMergeRefs<DataTableRef | HTMLDivElement>([
    forwardedRef,
    containerRef,
  ]);

  const configRowThresholds = useDataTableConfigElement('rowThresholds');

  useImperativeHandle(forwardedRef, () => ({
    element: containerRef.current,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    getConfig() {
      const options = table.options;
      const state = options.state;
      return {
        pagination: {
          defaultPageIndex: state.pagination?.pageIndex,
          defaultPageSize: state.pagination?.pageSize,
        },
        columnVisibility: state.columnVisibility,
        fullWidth: options.layout?.fullWidth,
        fullHeight: options.layout?.fullHeight,
        lineWrap: state.columnLineWrap,
        resizable: options.enableColumnResizing,
        selectableRows: options.enableRowSelection,
        // NOTE: typecast necessary due to generic resolving to unknown
        rowThresholds: (props.rowThresholds ?? configRowThresholds) as
          | DataTableRowThreshold<unknown>[]
          | undefined,
        sortable: options.enableSorting,
        variant: state.visualVariantConfig,
      };
    },
    downloadData(
      subset: 'all' | 'page' | 'selected',
      excludeColumns?: string[],
    ) {
      switch (subset) {
        case 'all':
          table.downloadAllData(excludeColumns);
          break;
        case 'page':
          table.downloadPageData(excludeColumns);
          break;
        case 'selected':
          table.downloadSelectedData(excludeColumns);
          break;
      }
    },
    scrollToRow: table.scrollToRow,
    getDisplayedRowIds(): string[] {
      const result: string[] = [];
      for (const row of table.getRowModel().rows) {
        if (!row[DATA_TABLE_DETAILS_ROW_IDENTIFIER]) {
          result.push(row.id);
        }
      }
      return result;
    },
    openColumnSettings: (columnSettings?: DataTableColumnSettingsConfig) => {
      columnSettingsModalRef.current?.openColumnSettings(columnSettings);
    },
  }));

  return rootRef;
}
